import React, { useContext, useEffect, useState } from 'react';
import ReportCanvasCard from './ReportCanvasCard';
import DashboardGadgetsContext from '../../contexts/DashboardGadgetsContext';
import ReportGadgetsContext from '../../contexts/ReportGadgetsContext';
import GadgetStatusBoundary from 'contextProviders/GadgetStatusBoundary';
import CardContext from 'contexts/CardContext';
import ReportContext from '../../contexts/ReportContext';
import HighchartStyles from '../HighchartStyles';
import BonusPeriodsProvider from '../../contextProviders/SplashScreenProviders/LegacyDriverBonusProviders/BonusPeriodsProvider';
import cardTypeCheckers from '../../types/cardTypeCheckers';

const ReportCanvasCardContainer = ({
  card,
  w,
  h,
  onRemoveClicked,
  isEditing,
  isDragging,
}: {
  card: CanvasCard.Card;
  // The card's displayed grid size, which differs from its authored layout
  // when the canvas is reflowed for a narrow screen.
  w: number;
  h: number;
  onRemoveClicked: () => void;
  isEditing: boolean;
  isDragging: boolean;
}) => {
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { reportGadgetsLookup } = useContext(ReportGadgetsContext);
  const [dashboardGadget, setDashboardGadget] = useState<
    DashboardGadget | undefined
  >();
  const [reportGadget, setReportGadget] = useState<
    ReportGadgets.ReportGadget | undefined
  >();
  const [chartDefinition, setChartDefinition] = useState<
    VisualisationDefinition | undefined
  >();

  useEffect(() => {
    setDashboardGadget(
      cardTypeCheckers.isDashboardGadget(card)
        ? dashboardGadgetsLookup[card.content.dashboardGadgetId]
        : undefined,
    );
  }, [card, dashboardGadgetsLookup]);

  useEffect(() => {
    setReportGadget(
      cardTypeCheckers.isReportGadget(card)
        ? reportGadgetsLookup[card.content.reportGadgetId]
        : undefined,
    );
  }, [card, reportGadgetsLookup]);

  useEffect(() => {
    setChartDefinition(
      dashboardGadget
        ? dashboardGadget.chartDef
        : reportGadget
          ? reportGadget.chartDef
          : undefined,
    );
  }, [card, dashboardGadget, reportGadget]);

  const { isExporting } = useContext(ReportContext);
  // Names the gadget in a scheduled render's logs and what it says is missing
  const name =
    chartDefinition?.name ||
    (cardTypeCheckers.isStudioComposite(card)
      ? card.content.composite.name
      : undefined) ||
    card.layout.i;

  return (
    // Positioned for GadgetStatusBoundary's error overlay
    <div
      id={card.layout.i}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      <CardContext.Provider
        value={{
          i: card.layout.i,
          w,
          h,
          chartDefinition,
        }}
      >
        <GadgetStatusBoundary id={card.layout.i} name={name}>
          <BonusPeriodsProvider>
            <CardOnClickProvider
              dashboardGadget={dashboardGadget}
              reportGadget={reportGadget}
              card={card}
              onRemoveClicked={onRemoveClicked}
              isEditing={isEditing}
              chartDefinition={chartDefinition}
              isExporting={!!isExporting}
              isDragging={isDragging}
            />
          </BonusPeriodsProvider>
        </GadgetStatusBoundary>
      </CardContext.Provider>
    </div>
  );
};

const CardOnClickProvider = ({
  dashboardGadget,
  reportGadget,
  card,
  onRemoveClicked,
  isEditing,
  chartDefinition,
  isExporting,
  isDragging,
}: {
  dashboardGadget: DashboardGadget | undefined;
  reportGadget: ReportGadgets.ReportGadget | undefined;
  card: CanvasCard.Card;
  onRemoveClicked: () => void;
  isEditing: boolean;
  chartDefinition: VisualisationDefinition | undefined;
  isExporting: boolean;
  isDragging: boolean;
}) => {
  return (
    <HighchartStyles style={{ width: '100%', height: '100%' }}>
      <ReportCanvasCard
        card={card}
        onRemoveClicked={onRemoveClicked}
        isEditing={isEditing}
        chartDefinition={chartDefinition}
        dashboardGadget={dashboardGadget}
        reportGadget={reportGadget}
        isExporting={!!isExporting}
        isDragging={isDragging}
      />
    </HighchartStyles>
  );
};

export default ReportCanvasCardContainer;
